import type { ChangeEvent } from 'react';
import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { FiatWalletAccountState } from '#src/__generated__/globalTypes.ts';
import { RadioGroup } from '#src/atoms/inputs/RadioGroup/index.tsx';
import { Switch } from '#src/atoms/inputs/Switch/index.tsx';
import { SBHorizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM } from '#src/atoms/typography/index.tsx';
import { NeedsValidatedFiatWalletHelper } from '#src/components/fiatWallet/NeedsValidatedFiatWalletHelper/index.tsx';
import { SettingsSection } from '#src/components/settings/SettingsSection/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { AcceptedCurrenciesValue } from '#src/hooks/useAcceptedCurrencies.ts';
import { useCurrentUserAcceptedCurrencies } from '#src/hooks/useCurrentUserAcceptedCurrencies.ts';
import { useCurrentUserCurrencies } from '#src/hooks/useCurrentUserCurrencies.tsx';
import { useReferenceCurrencyForSale } from '#src/hooks/useReferenceCurrencyForSale.ts';
import { useFiatBalance } from '#src/hooks/wallets/useFiatBalance.ts';
import { isFiatCurrency } from '#src/lib/fiat.ts';
import { payment } from '#src/lib/glossary.ts';
import { EnterCashWalletKYCFlow_Source } from '#src/protos/events/platform/web/events.ts';

import { ConfirmationDialog } from './ConfirmationDialog/index.tsx';
import { messages } from './messages.ts';

const OptionalRoot = styled.div`
  padding: 0 !important;
`;
const OptionalBlock = styled.div`
  padding: 0 var(--double-unit) var(--double-unit);
`;
const Optional = styled(SBHorizontal)`
  background-color: var(--c-nd-200);
  padding: var(--double-unit);
  border-radius: var(--unit);
`;

const sectionMessages = defineMessages({
  title: {
    defaultMessage: 'How you’ll get paid',
  },
  description: {
    defaultMessage:
      'Decide how you’ll be paid for cards listed on the Marketplace as well as from direct offers from other Managers. <bold>This will only apply to future listings (current listings will follow previous payment settings)</bold>.',
  },
});

const Helper = ({ source }: { source: EnterCashWalletKYCFlow_Source }) => (
  <NeedsValidatedFiatWalletHelper
    withPadding
    source={source}
    statusTarget={FiatWalletAccountState.OWNER}
    defaultHelper={
      <FormattedMessage defaultMessage="You’ll need to activate your Cash Wallet to receive payments in cash." />
    }
  />
);

export const AcceptedCurrencies = () => {
  const {
    walletPreferences: { showCryptoWallet, showFiatWallet },
  } = useCurrentUserContext();
  const { canListAndTrade } = useFiatBalance();
  const { cryptocurrency, fiatCurrency, cryptocurrencySymbol } =
    useCurrentUserCurrencies();
  const { acceptedCurrencies, updateAcceptedCurrencies } =
    useCurrentUserAcceptedCurrencies();
  const [askForUpdateAcceptedCurrencies, setAskForUpdateAcceptedCurrencies] =
    useState<AcceptedCurrenciesValue | undefined>(undefined);
  const { referenceCurrencyForSale, update: updateReferenceCurrencyForSale } =
    useReferenceCurrencyForSale();

  const onReferenceCurrencyForSaleChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      updateAcceptedCurrencies(AcceptedCurrenciesValue.BOTH);
      setAskForUpdateAcceptedCurrencies(undefined);
    }
    updateReferenceCurrencyForSale(
      event.target.checked ? cryptocurrency : fiatCurrency
    );
  };

  const onChange = (value: AcceptedCurrenciesValue) => {
    if (value === AcceptedCurrenciesValue.CRYPTO) {
      setAskForUpdateAcceptedCurrencies(value);
    } else {
      updateAcceptedCurrencies(value);
      setAskForUpdateAcceptedCurrencies(undefined);
    }
  };
  const options = [
    {
      value: AcceptedCurrenciesValue.BOTH,
      label: (
        <BodyM color="var(--c-white)">
          <FormattedMessage {...messages.cashAndCrypto} />
        </BodyM>
      ),
      helper: (
        <Helper
          source={
            EnterCashWalletKYCFlow_Source.SETTINGS_CASH_AND_ETH_RECEIVED_PAYMENT_HELPER
          }
        />
      ),
      disabled: !canListAndTrade,
      optional: (
        <OptionalRoot>
          <OptionalBlock>
            <Optional>
              <Vertical gap={0}>
                <BodyM bold color="var(--c-white)">
                  <FormattedMessage
                    {...payment.peggingTitle}
                    values={{
                      cryptocurrencySymbol,
                    }}
                  />
                </BodyM>
                <BodyM color="var(--c-nd-600)">
                  <FormattedMessage
                    {...payment.peggingDescription}
                    values={{
                      cryptocurrencySymbol,
                    }}
                  />
                </BodyM>
              </Vertical>
              <Switch
                name="reference-currency-for-sale"
                checked={
                  acceptedCurrencies === AcceptedCurrenciesValue.BOTH &&
                  !isFiatCurrency(referenceCurrencyForSale)
                }
                onChange={onReferenceCurrencyForSaleChange}
                $backgroundColor="var(--c-nd-400)"
              />
            </Optional>
          </OptionalBlock>
        </OptionalRoot>
      ),
    },
    {
      value: AcceptedCurrenciesValue.FIAT,
      label: (
        <BodyM color="var(--c-white)">
          <FormattedMessage {...messages.fiat} />
        </BodyM>
      ),
      helper: (
        <Helper
          source={
            EnterCashWalletKYCFlow_Source.SETTINGS_CASH_ONLY_RECEIVED_PAYMENT_HELPER
          }
        />
      ),
      disabled: !canListAndTrade,
    },
    {
      value: AcceptedCurrenciesValue.CRYPTO,
      label: (
        <BodyM color="var(--c-white)">
          <FormattedMessage {...messages.crypto} />
        </BodyM>
      ),
    },
  ];

  if (!showCryptoWallet || !showFiatWallet) return null;

  return (
    <SettingsSection {...sectionMessages}>
      <RadioGroup
        withSpacing
        options={options}
        value={acceptedCurrencies || AcceptedCurrenciesValue.CRYPTO}
        name="currency"
        onChange={onChange}
      />

      <ConfirmationDialog
        open={
          !!askForUpdateAcceptedCurrencies &&
          askForUpdateAcceptedCurrencies !== acceptedCurrencies
        }
        value={askForUpdateAcceptedCurrencies}
        onConfirm={() => {
          updateAcceptedCurrencies(askForUpdateAcceptedCurrencies!);
        }}
        onCancel={() => {
          setAskForUpdateAcceptedCurrencies(acceptedCurrencies);
        }}
      />
    </SettingsSection>
  );
};
